import { BaseTable } from "@/components/BaseTable";
import {
  IActivityLog,
  IActivityLogResponseById,
} from "@/utils/types/activityLogs";
import { useQuery } from "@tanstack/react-query";
import ActivityLogsService from "@/services/FinEaseHome/APIs/ActivityLogsService";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { gridName } from "@/utils/constant/gridName";
import { getTenantId } from "@/lib/storeManager";
import { formatActivityDate } from "@/utils/constant/dateUtils";
import CustomButton from "@/components/common/Button";
import { Eye } from "lucide-react";
import CommonModal from "@/components/common/CommonModal";
import ActivityLogModal from "./ActivityLogModal";
import FinEaseHomeRoutes from "@/projects/FinEaseHome/utils/navigationRoutes";

const ActivityTable = () => {
  const navigate = useNavigate();
  const tenantId = getTenantId();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const [showModal, setShowModal] = useState(false);
  const [activityLog, setActivityLog] = useState<IActivityLog | null>(null);
  const {
    data,
    isLoading,
    refetch: refetchActivityLogs,
  } = useQuery({
    queryKey: ["activityLogs", tenantId, page, pageSize],
    queryFn: () => ActivityLogsService.listActivityLogs(page, pageSize),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const handleView = async (id: string | undefined) => {
    if (!id) {
      return;
    }

    const response = await ActivityLogsService.getActivityLog(id);

    if (response) {
      setActivityLog(response as IActivityLog);
      setShowModal(true);
    }
  };

  const columns: ProColumns<IActivityLog>[] = [
    {
      title: "User",
      dataIndex: "user_name",
    },
    {
      title: "Activity Type",
      dataIndex: "activity_type",
    },
    {
      title: "Activity Title",
      dataIndex: "activity_title",
    },
    {
      title: "Timestamp",
      dataIndex: "created_on",
      render: (_, record) => formatActivityDate(record.created_on ?? ""),
      valueType: "dateTime",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="view"
          icon={<Eye size={16} />}
          type="text"
          title="View"
          onClick={() => handleView(record.id ?? "")}
        />,
      ],
    },
  ];

  return (
    <>
      <div className="mt-6 rounded-xl border bg-white p-5 shadow-sm">
        <BaseTable<IActivityLog>
          loading={isLoading}
          columns={columns}
          dataSource={(data?.results ?? []) as IActivityLog[]}
          page={page}
          pageSize={pageSize}
          total={10}
          onPageChange={(page, pageSize) => {
            setPage(page);
            setPageSize(pageSize ?? 10);
            searchParams.set("page", page.toString());
            searchParams.set("pageSize", pageSize.toString());
            navigate(
              `${FinEaseHomeRoutes.ACTIVITY_LOG}?${searchParams.toString()}`
            );
            refetchActivityLogs();
          }}
          rowKey="id"
          headerTitle="Recent Activity"
          toolBarRender={undefined}
          request={() => refetchActivityLogs()}
          tableName={gridName.activityLogsTable}
        />
      </div>
      <CommonModal
        open={showModal}
        onCancel={() => setShowModal(false)}
        title="Activity Log"
        children={
          <ActivityLogModal
            activityLog={activityLog as IActivityLogResponseById}
          />
        }
        onOk={() => setShowModal(false)}
        okText="Close"
        cancelText="Close"
        confirmLoading={false}
        footer={null}
        width={800}
      />
    </>
  );
};

export default ActivityTable;
